/* eslint-disable no-console */
import { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getToken } from "../Utils/helper";
import { buildTicketedWebSocketURL } from "../api/api";
import { clearWebSocketStatus, handleWebSocketClose } from "../auth/websocketLifecycle";
import {
  setConnectionStatus,
  incrementUnreadCount,
} from "../Redux/client-communication/clientCommunicationSlice";

const ClientCommunicationWebSocketContext = createContext(null);

export const useClientCommunicationWebSocket = () => {
  const context = useContext(ClientCommunicationWebSocketContext);
  if (!context) {
    throw new Error(
      "useClientCommunicationWebSocket must be used within ClientCommunicationWebSocketProvider"
    );
  }
  return context;
};

/**
 * Global WebSocket provider for client communications
 * Maintains WebSocket connection even when navigating away from communication tab
 * Dispatches notification count updates to Redux store
 */
export const ClientCommunicationWebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const connectionAttemptRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const currentClientRef = useRef(null);

  // Get active client and communication tab status from Redux
  const activeClient = useSelector((state) => state.clientCommunication.activeClient);
  const isCommunicationTabOpen = useSelector(
    (state) => state.clientCommunication.isCommunicationTabOpen
  );

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 1000;
  const MAX_RECONNECT_DELAY = 30000;

  // Calculate reconnection delay with exponential backoff
  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY
    );
    return delay;
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("📨 Global WebSocket message received:", message);

        // Handle error messages
        if (message.type === "error") {
          console.error("❌ WebSocket error message:", message.message);
          if (message.message.includes("Token expired")) {
            wsRef.current?.close(4401, "Authentication failed");
          }
          return;
        }

        // The WebSocket is connected to a specific client/case endpoint
        // Extract the client/case from currentClientRef (format: "clientId_caseId")
        if (!currentClientRef.current) {
          console.warn("No current client ref, skipping message");
          return;
        }

        const [clientId, caseId] = currentClientRef.current.split("_");

        // Determine if we should increment notification count
        // Only increment if NOT viewing this client's communication tab
        const isViewingThisClientComm =
          activeClient === currentClientRef.current && isCommunicationTabOpen;
        console.log(message);
        console.log(activeClient);
        console.log(currentClientRef.current);
        console.log(isCommunicationTabOpen);
        if (!isViewingThisClientComm) {
          // Map message types to communication types
          const typeMap = {
            incoming_text_message: "text",
            outgoing_text_message: "text",
            TextMessage: "text",
            IncomingTextMessage: "text",
            InboundRecordedCall: "phone",
            OutboundRecordedCall: "phone",
            email: "email",
            chat_message: "chat",
            phone_call_log: "phone",
            letter_log: "letter",
          };

          const commType = typeMap[message.type];
          console.log(commType);
          if (commType) {
            dispatch(
              incrementUnreadCount({
                clientId,
                caseId,
                type: commType,
              })
            );
          }
        }

        // Broadcast message to any component listeners
        window.dispatchEvent(
          new CustomEvent("clientCommunicationMessage", {
            detail: message,
          })
        );
      } catch (error) {
        console.error("❌ Error parsing WebSocket message:", error);
      }
    },
    [dispatch, activeClient, isCommunicationTabOpen]
  );

  // Connect to WebSocket for a specific client
  const connect = useCallback(
    async (clientId, caseId) => {
      const token = getToken();

      if (!clientId || !caseId || !token) {
        console.warn("Missing required parameters for WebSocket connection");
        return;
      }

      const newClientKey = `${clientId}_${caseId}`;

      // Ticket issuance is asynchronous. Redux updates can rerun the page
      // effect before wsRef is assigned, so also deduplicate the in-flight
      // attempt for this client/case pair.
      if (
        connectionAttemptRef.current?.clientKey === newClientKey ||
        (wsRef.current &&
          [WebSocket.CONNECTING, WebSocket.OPEN].includes(wsRef.current.readyState) &&
          currentClientRef.current === newClientKey)
      ) {
        console.log("✅ Already connecting or connected to this client");
        return;
      }

      const connectionAttempt = { clientKey: newClientKey };
      connectionAttemptRef.current = connectionAttempt;
      currentClientRef.current = newClientKey;

      // Clean up existing connection
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        console.log("🔌 Closing existing WebSocket connection");
        const previousSocket = wsRef.current;
        wsRef.current = null;
        previousSocket.close(1000, "Switching to new client");
      }

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      try {
        const path = `/ws/client-communication/${clientId}/${caseId}/`;
        const wsUrl = await buildTicketedWebSocketURL(path);

        // A navigation or explicit disconnect superseded this ticket request.
        if (connectionAttemptRef.current !== connectionAttempt) {
          return;
        }

        console.log("📡 Connecting Global WebSocket");

        const websocket = new WebSocket(wsUrl);

        websocket.onopen = () => {
          if (wsRef.current !== websocket || currentClientRef.current !== newClientKey) {
            websocket.close(1000, "Superseded connection");
            return;
          }
          console.log("✅ Global WebSocket connected successfully");
          dispatch(setConnectionStatus({ clientId, caseId, isConnected: true }));
          reconnectAttemptsRef.current = 0;
          clearWebSocketStatus(path);
        };

        websocket.onmessage = handleMessage;

        websocket.onerror = (error) => {
          console.error("❌ Global WebSocket error:", error);
        };

        websocket.onclose = (event) => {
          if (wsRef.current !== websocket) {
            return;
          }
          wsRef.current = null;
          console.log("Global WebSocket closed:", event.code, event.reason);
          dispatch(setConnectionStatus({ clientId, caseId, isConnected: false }));

          if (!handleWebSocketClose(event, path)) {
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
            return;
          }

          // Don't reconnect if closed intentionally.
          if (event.code === 1000) {
            console.log("WebSocket closed intentionally, not reconnecting");
            return;
          }

          // Only attempt to reconnect if this is still the current client
          if (
            currentClientRef.current === newClientKey &&
            reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
          ) {
            const delay = getReconnectDelay();
            console.log(
              `🔄 Reconnecting in ${delay}ms... (Attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectTimeoutRef.current = null;
              reconnectAttemptsRef.current += 1;
              connect(clientId, caseId);
            }, delay);
          } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            console.error("❌ Max reconnection attempts reached");
          }
        };

        wsRef.current = websocket;
        connectionAttemptRef.current = null;
      } catch (error) {
        if (connectionAttemptRef.current === connectionAttempt) {
          connectionAttemptRef.current = null;
          if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
            currentClientRef.current = null;
          }
        }
        console.error("❌ Error creating WebSocket connection:", error);
      }
    },
    [dispatch, handleMessage, getReconnectDelay]
  );

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    // Invalidate a ticket request that has not created its socket yet.
    connectionAttemptRef.current = null;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      console.log("🔌 Disconnecting Global WebSocket");
      const websocket = wsRef.current;
      wsRef.current = null;
      websocket.close(1000, "Disconnecting");
    }

    currentClientRef.current = null;
    dispatch(setConnectionStatus({ clientId: null, caseId: null, isConnected: false }));
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const value = {
    connect,
    disconnect,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };

  return (
    <ClientCommunicationWebSocketContext.Provider value={value}>
      {children}
    </ClientCommunicationWebSocketContext.Provider>
  );
};
